import { SpacingValue } from ".";

/**
 * Mapping of padding variants to their corresponding Tailwind classes
 * These classes are statically defined to ensure proper detection by Tailwind's JIT compiler
 *
 * @example
 * // For top padding, full variant:
 * paddingClassesVariant.top.full === 'pt-80 md:pt-140'
 */
const paddingClassesVariant = {
  top: {
    full: 'pt-80 md:pt-140',
    half: 'pt-40 md:pt-70',
    disabled: 'pt-0',
  },
  bottom: {
    full: 'pb-80 md:pb-140',
    half: 'pb-40 md:pb-70',
    disabled: 'pb-0',
  },
} as const;

/**
 * Generates padding classes for a section based on top and bottom spacing configurations
 *
 * @param topPadding - Spacing configuration for the top of the section
 * @param bottomPadding - Spacing configuration for the bottom of the section
 * @returns String of Tailwind classes for padding
 *
 * @example
 * // Returns classes for full padding top and bottom
 * applyPadding({ topPadding: 'full', bottomPadding: 'full' })
 * // => 'pt-80 md:pt-140 pb-80 md:pb-140'
 *
 * // Returns classes for custom padding
 * applyPadding({
 *   topPadding: { mobile: 48, desktop: 48 },
 *   bottomPadding: 'half'
 * })
 * // => 'pt-48 md:pt-48 pb-40 md:pb-70'
 */
export const applyPadding = ({
  topPadding,
  bottomPadding,
}: {
  topPadding: SpacingValue;
  bottomPadding: SpacingValue;
}): string => {
  /**
   * Generates the appropriate padding class based on spacing value and position
   *
   * @param spacing - The spacing configuration (preset or custom)
   * @param position - Whether this is for top or bottom padding
   * @returns Tailwind classes for the specified padding
   */
  const getSpacingClass = (
    spacing: SpacingValue,
    position: 'top' | 'bottom',
  ) => {
    if (typeof spacing === 'object') {
      const { mobile, desktop } = spacing;
      const prefix = position === 'top' ? 'pt' : 'pb';
      return `${prefix}-${mobile} md:${prefix}-${desktop}`;
    }

    return paddingClassesVariant[position][spacing];
  };

  const top = getSpacingClass(topPadding, 'top');
  const bottom = getSpacingClass(bottomPadding, 'bottom');

  return `${top} ${bottom}`.trim();
};
